/**
 * Log a visit / call note for an account.
 *
 * Modal opened from the account-detail page and the Today's Plan
 * "Log Visit" CTA.  Drives a POST to /api/field/accounts/{id}/notes,
 * then invalidates the surfaces that depend on the new row (account
 * detail, Today's Plan, the follow-up tracker, admin activity) so
 * cooldown + the "Logged today" row state reflect the fresh write
 * without a manual refresh.
 *
 * Above the form, the dialog renders a "Previous touchpoints"
 * timeline pulled from the same /notes endpoint — every prior
 * visit / call on this account from any rep, newest first — so the
 * rep can walk in knowing what was said last time.
 *
 * Pass `note` to edit a visit that's already logged instead: the form
 * prefills from it and saves with a PUT that replaces the whole note.
 * The touchpoints timeline is hidden in that mode — the rep is
 * correcting one entry, not preparing for a visit.
 *
 * A rep can also edit one of their own touchpoints from inside the Log
 * Visit form — it's where they notice last time's note was wrong. The
 * new visit they'd started is set aside meanwhile and comes back once
 * the edit is saved or abandoned.
 */

import CallIcon from '@mui/icons-material/Call';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { OUTCOME_GUIDE } from '../../lib/visitOutcomes';

import {
  type FieldAccountSummary,
  type VisitChannel,
  type VisitNote,
  type VisitNoteDraft,
  type VisitOutcome,
  VISIT_CHANNEL_LABELS,
  VISIT_OUTCOME_LABELS,
  createVisitNote,
  updateVisitNote,
  fetchProductOptions,
  fetchAccountDetail,
} from '../../api/field';
import { parseBackendDate } from '../../util/dates';
import { EditedMarker } from './EditedMarker';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const NOTE_MAX = 10_000;

// Brand-tone palette per outcome — mirrors the Today's Plan row
// chips so the same outcome is visually consistent across surfaces.
const OUTCOME_TONE: Record<VisitOutcome, string> = {
  ordered: '#22865a',
  follow_up_needed: '#a86b1e',
  no_response: '#7a7a78',
  declined: '#a13a3a',
  info_only: '#326eb8',
};

// visit_date is YYYY-MM-DD; compute the resurface date by adding
// the cooldown to that day in UTC (avoids the timezone-shift bug
// where Date parsing of "2026-06-17" treats it as midnight UTC and
// then shifts back by the rep's tz).
function formatEligibleDate(visitDateIso: string, cooldownDays: number): string {
  const [y, m, d] = visitDateIso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return visitDateIso;
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + cooldownDays);
  return base.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Everything the form holds, so a started visit can be set aside while
// the rep edits an earlier one.
interface FormValues {
  visitDate: string;
  channel: VisitChannel;
  outcome: VisitOutcome;
  noteText: string;
  productLines: Record<number, string>;
}

function formatVisitDate(iso: string): string {
  const d = parseBackendDate(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function blankForm(today: string): FormValues {
  return { visitDate: today, channel: 'visit', outcome: 'ordered', noteText: '', productLines: {} };
}

function formFromNote(note: VisitNoteDraft): FormValues {
  return {
    visitDate: note.visit_date,
    channel: note.channel,
    outcome: note.outcome,
    noteText: note.note_text,
    // "2.50" comes back from the API; show it the way the rep typed it.
    productLines: Object.fromEntries(
      note.products.map((p) => [
        p.product_id,
        p.quantity === null ? '' : String(Number(p.quantity)),
      ]),
    ),
  };
}

interface VisitNoteDialogProps {
  open: boolean;
  accountId: number;
  accountName: string;
  /** The logged visit to edit. Omit to log a new one. */
  note?: VisitNoteDraft | null;
  onClose: () => void;
  /** Optional callback fired after the visit note is successfully
   *  written to the backend.  Used by Today's Plan to drive a brand
   *  success Snackbar — "Logged · {accountName}" — so the rep gets
   *  an explicit moment-of-confirmation in addition to the passive
   *  row fade + scorecard tick. */
  onSuccess?: (info: { outcome: VisitOutcome; channel: VisitChannel; accountName: string }) => void;
}

const CHANNELS: VisitChannel[] = ['visit', 'call', 'email', 'event'];

// Icon per channel, shared by the picker and the history timeline so a
// logged note reads the same in both places.
const CHANNEL_ICON: Record<VisitChannel, typeof CallIcon> = {
  visit: StorefrontIcon,
  call: CallIcon,
  email: EmailIcon,
  event: EventIcon,
};
const OUTCOMES: VisitOutcome[] = [
  'ordered',
  'follow_up_needed',
  'no_response',
  'declined',
  'info_only',
];

export function VisitNoteDialog({
  open,
  accountId,
  accountName,
  note = null,
  onClose,
  onSuccess,
}: VisitNoteDialogProps) {
  // A touchpoint opened for editing from inside the Log Visit form.
  const [inlineEdit, setInlineEdit] = useState<VisitNoteDraft | null>(null);
  // The new visit the rep had started before opening that edit.
  const [setAside, setSetAside] = useState<FormValues | null>(null);
  const editing = note ?? inlineEdit;
  const isEdit = editing !== null;
  const queryClient = useQueryClient();
  // Mobile-first: dialog goes full-screen on phones so reps logging
  // a visit on a 5-inch device get a real form, not a cramped modal.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const today = new Date().toISOString().slice(0, 10);

  // Callers mount the dialog per note, so the initial state is the
  // only prefill needed.
  const [initial] = useState(() => (note ? formFromNote(note) : blankForm(today)));
  const [visitDate, setVisitDate] = useState(initial.visitDate);
  const [channel, setChannel] = useState<VisitChannel>(initial.channel);
  const [outcome, setOutcome] = useState<VisitOutcome>(initial.outcome);
  const [noteText, setNoteText] = useState(initial.noteText);
  // Rep-reported product lines. Keyed by product id so toggling a
  // SKU on and off doesn't leave a stale quantity behind.
  const [productLines, setProductLines] = useState<Record<number, string>>(initial.productLines);
  const [historyOpen, setHistoryOpen] = useState(true);

  // Account detail bundles the address summary + prior visit notes
  // in a single round-trip.  Shares the TanStack cache key with the
  // detail page so opening this dialog right after viewing that
  // page is free.  Only fetched while the dialog is open.
  const detailQuery = useQuery({
    queryKey: ['field', 'account', accountId],
    queryFn: () => fetchAccountDetail(accountId),
    enabled: open,
    staleTime: 30_000,
  });
  // The SKU catalog — 11 products, stable, so cached long.
  const productsQuery = useQuery({
    queryKey: ['field', 'product-options'],
    queryFn: fetchProductOptions,
    staleTime: 30 * 60_000,
    enabled: open,
  });

  const summary = detailQuery.data?.summary ?? null;
  const priorNotes = detailQuery.data?.notes ?? [];

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        visit_date: visitDate,
        channel,
        outcome,
        note_text: noteText.trim(),
        // Only SKUs the rep actually selected. A blank quantity is
        // meaningful ("we discussed the Rye"), so it's sent as null
        // rather than dropping the line.
        products: Object.entries(productLines).map(([productId, qty]) => ({
          product_id: Number(productId),
          quantity: qty.trim() === '' ? null : Number(qty),
        })),
      };
      return editing
        ? updateVisitNote(accountId, editing.id, payload)
        : createVisitNote(accountId, payload);
    },
    onSuccess: () => {
      // Invalidate everything that may show this account's visit
      // state.  Without the follow-up + today-plan keys, the
      // "Logged today" badge + row fade on Today's Plan would only
      // appear on the next manual refresh.
      queryClient.invalidateQueries({ queryKey: ['field', 'account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['field', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'today-plan'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'follow-up'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'field', 'activity'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'my-logs'] });
      // The whole point of the momentum panel is that logging a
      // visit visibly moves it. Without this the rep files a note,
      // returns to the page, and sees the same number.
      queryClient.invalidateQueries({ queryKey: ['field', 'momentum'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'field', 'momentum'] });
      // An edit made from the touchpoints list goes back to the visit
      // the rep was logging, rather than closing on them.
      if (inlineEdit) {
        endInlineEdit();
        return;
      }
      // Fire the optional success callback BEFORE closing so the
      // caller can capture the outcome + channel for a Snackbar
      // message.  Capturing post-close would race with the form
      // reset in handleClose.
      onSuccess?.({ outcome, channel, accountName });
      handleClose();
    },
  });

  const formValues = (): FormValues => ({
    visitDate,
    channel,
    outcome,
    noteText,
    productLines,
  });
  const fillForm = (v: FormValues) => {
    setVisitDate(v.visitDate);
    setChannel(v.channel);
    setOutcome(v.outcome);
    setNoteText(v.noteText);
    setProductLines(v.productLines);
  };

  const startInlineEdit = (n: VisitNote) => {
    setSetAside(formValues());
    setInlineEdit(n);
    fillForm(formFromNote(n));
    mutation.reset();
  };

  const endInlineEdit = () => {
    if (setAside) fillForm(setAside);
    setSetAside(null);
    setInlineEdit(null);
    mutation.reset();
  };

  const handleClose = () => {
    fillForm(blankForm(today));
    setInlineEdit(null);
    setSetAside(null);
    setHistoryOpen(true);
    mutation.reset();
    onClose();
  };

  const valid = noteText.trim().length > 0 && visitDate.length === 10;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            bgcolor: '#fdf9f0',
            borderRadius: { xs: 0, sm: 2 },
            border: { xs: 'none', sm: '1px solid rgba(187, 140, 63, 0.22)' },
          },
        },
      }}
    >
      {/* Two-column title — gold "LOG VISIT" eyebrow on the left,
          subject (account name + address) on the right.  Splits the
          action from its target visually and pulls the address up
          alongside the name so the rep can confirm the storefront at
          a glance.  Stacks on mobile. */}
      <DialogTitle
        sx={{
          fontFamily: FONT_INTER,
          color: INK,
          py: { xs: 2, sm: 2.25 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
          alignItems: 'center',
          columnGap: { sm: 3 },
          rowGap: { xs: 1, sm: 0 },
          borderRight: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: { sm: 2.5 },
            borderRight: { sm: '1px solid rgba(187, 140, 63, 0.28)' },
            minHeight: { sm: 44 },
          }}
        >
          <EditNoteIcon sx={{ color: GOLD, fontSize: 22 }} />
          <Typography
            component="span"
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: GOLD,
              whiteSpace: 'nowrap',
            }}
          >
            {isEdit ? 'Edit Visit' : 'Log Visit'}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 16,
              fontWeight: 700,
              color: INK,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {accountName}
          </Typography>
          <AccountAddressLine summary={summary} loading={detailQuery.isLoading} />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: 'rgba(187, 140, 63, 0.22)', py: 2 }}>
        <Stack spacing={1.75}>
          {!isEdit && (
            <PreviousTouchpoints
              notes={priorNotes}
              loading={detailQuery.isLoading}
              open={historyOpen}
              onToggle={() => setHistoryOpen((p) => !p)}
              onEdit={startInlineEdit}
            />
          )}

          {inlineEdit && (
            <Alert severity="info" sx={{ fontFamily: FONT_INTER }}>
              Editing your {formatVisitDate(inlineEdit.visit_date)} touchpoint. The new visit you
              started is set aside — you’ll be back to it after saving.
            </Alert>
          )}

          {mutation.error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
              {(mutation.error as Error).message}
            </Alert>
          )}

          {/* Single-row grid: date, channel, outcome.  Visit date is
              a short fixed input so it stops eating the row; channel
              has two options so it stays narrow; outcome carries the
              most semantic weight and gets the most width. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '170px 1fr 1.6fr',
              },
              gap: 2,
            }}
          >
            <TextField
              label="Visit date"
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Channel"
              select
              value={channel}
              onChange={(e) => setChannel(e.target.value as VisitChannel)}
              size="small"
            >
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c}>
                  {VISIT_CHANNEL_LABELS[c]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Outcome"
              select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as VisitOutcome)}
              fullWidth
              size="small"
              // renderValue keeps the COLLAPSED Select compact —
              // "Ordered · 30d cooldown" — while the dropdown options
              // can be rich two-line cards with the full explainer.
              SelectProps={{
                renderValue: (value) => {
                  const g = OUTCOME_GUIDE[value as VisitOutcome];
                  return `${g.label} · ${g.cooldownDays}d cooldown`;
                },
                MenuProps: {
                  PaperProps: { sx: { maxWidth: 380 } },
                },
              }}
            >
              {OUTCOMES.map((o) => {
                const guide = OUTCOME_GUIDE[o];
                const tone = OUTCOME_TONE[o];
                return (
                  <MenuItem
                    key={o}
                    value={o}
                    sx={{
                      alignItems: 'flex-start',
                      py: 1,
                      whiteSpace: 'normal',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: FONT_INTER,
                          fontSize: 13,
                          fontWeight: 700,
                          color: INK,
                          lineHeight: 1.3,
                        }}
                      >
                        {guide.label}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontFamily: FONT_INTER,
                          fontSize: 11.5,
                          color: 'rgba(15, 10, 7, 0.6)',
                          lineHeight: 1.45,
                        }}
                      >
                        {guide.when}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${guide.cooldownDays}d`}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        height: 20,
                        fontSize: 10.5,
                        fontFamily: FONT_INTER,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        bgcolor: `${tone}1a`,
                        color: tone,
                        border: `1px solid ${tone}55`,
                        '& .MuiChip-label': { px: 0.85 },
                      }}
                    />
                  </MenuItem>
                );
              })}
            </TextField>
          </Box>

          <NextVisitImpactCard
            outcome={outcome}
            visitDate={visitDate}
            channel={channel}
            isEdit={isEdit}
          />

          <TextField
            label="Note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder="What did you discuss? Who did you speak with? Next steps? Note any nuance the next rep should know — buyer on vacation, new ownership, distribution delay, sample preferences, etc."
            inputProps={{ maxLength: NOTE_MAX }}
            helperText={`${noteText.length.toLocaleString()} / ${NOTE_MAX.toLocaleString()}`}
          />

          {/* Rep-reported products. Deliberately separate from the
              depletions data on the account: this is what the REP says
              moved, recorded against the visit that produced it. It is
              never written into depletions.facts and must never be
              summed with the 9L figures — those are the distributor's
              record and the whole dashboard reads them on that basis. */}
          <Box>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: GOLD,
                mb: 0.5,
              }}
            >
              Products
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12,
                color: 'rgba(15, 10, 7, 0.55)',
                mb: 1.25,
              }}
            >
              What moved or came up. Leave the quantity blank if you only discussed it.
            </Typography>

            {productsQuery.isPending ? (
              <CircularProgress size={18} />
            ) : (
              <Stack gap={0.75}>
                {(productsQuery.data?.products ?? []).map((product) => {
                  const selected = product.product_id in productLines;
                  return (
                    <Stack
                      key={product.product_id}
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1.25,
                        py: 0.6,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selected ? 'rgba(187, 140, 63, 0.42)' : 'rgba(15,10,7,0.10)',
                        bgcolor: selected ? 'rgba(187, 140, 63, 0.06)' : 'transparent',
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selected}
                        onChange={(e) =>
                          setProductLines((prev) => {
                            const next = { ...prev };
                            // Removing the key rather than blanking it
                            // keeps a stale quantity from being sent if
                            // the rep unticks and re-ticks a SKU.
                            if (e.target.checked) next[product.product_id] = '';
                            else delete next[product.product_id];
                            return next;
                          })
                        }
                        sx={{ p: 0.5, color: GOLD, '&.Mui-checked': { color: GOLD } }}
                      />
                      <Typography
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          fontFamily: FONT_INTER,
                          fontSize: 13,
                          fontWeight: selected ? 600 : 400,
                          color: INK,
                        }}
                      >
                        {product.name}
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Cases"
                        value={productLines[product.product_id] ?? ''}
                        onChange={(e) =>
                          setProductLines((prev) => ({
                            ...prev,
                            [product.product_id]: e.target.value,
                          }))
                        }
                        // Ticking the box is what includes the line;
                        // typing a quantity implies it too.
                        onFocus={() =>
                          setProductLines((prev) =>
                            product.product_id in prev
                              ? prev
                              : { ...prev, [product.product_id]: '' },
                          )
                        }
                        inputProps={{
                          inputMode: 'decimal',
                          'aria-label': `Quantity for ${product.name}`,
                        }}
                        sx={{ width: 92, '& input': { fontSize: 13, py: 0.6 } }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={inlineEdit ? endInlineEdit : handleClose}>
          {inlineEdit ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || mutation.isPending}
          onClick={() => mutation.mutate()}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// =====================================================================
// Previous touchpoints — chronological history of visit notes for
// this account.  Renders an empty-state nudge ("you're the first")
// when nothing prior exists; a count chip + collapsible card
// otherwise.  Scrolls when the list grows long.
// =====================================================================
function PreviousTouchpoints({
  notes,
  loading,
  open,
  onToggle,
  onEdit,
}: {
  notes: VisitNote[];
  loading: boolean;
  open: boolean;
  onToggle: () => void;
  onEdit: (note: VisitNote) => void;
}) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1.5,
          color: 'rgba(15, 10, 7, 0.4)',
        }}
      >
        <CircularProgress size={18} sx={{ color: GOLD }} />
      </Box>
    );
  }

  if (notes.length === 0) {
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: 1.25,
          bgcolor: 'rgba(187, 140, 63, 0.06)',
          border: '1px dashed rgba(187, 140, 63, 0.32)',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: GOLD,
            mb: 0.5,
          }}
        >
          No previous touchpoints
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.62)',
            lineHeight: 1.5,
          }}
        >
          You’re the first rep to log a touchpoint on this account. Capture the buyer’s name and any
          preferences so the next visit can build on it.
        </Typography>
      </Box>
    );
  }

  // Sort newest-first defensively in case the API returns in a
  // different order on some routes.
  const sorted = [...notes].sort((a, b) =>
    a.visit_date < b.visit_date ? 1 : a.visit_date > b.visit_date ? -1 : 0,
  );

  return (
    <Box
      sx={{
        borderRadius: 1.25,
        bgcolor: 'rgba(187, 140, 63, 0.05)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          borderBottom: open ? '1px solid rgba(187, 140, 63, 0.18)' : 'none',
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.04)' },
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          Previous touchpoints
        </Typography>
        <Chip
          label={sorted.length}
          size="small"
          sx={{
            height: 18,
            fontSize: 10.5,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            bgcolor: 'rgba(187, 140, 63, 0.18)',
            color: INK,
            border: '1px solid rgba(187, 140, 63, 0.4)',
            '& .MuiChip-label': { px: 0.85 },
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          {open ? 'Hide' : 'Show'}
        </Typography>
        <IconButton size="small" sx={{ color: GOLD, p: 0.25 }}>
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            maxHeight: 260,
            overflowY: 'auto',
            px: 1.5,
            py: 1,
            // Brand scrollbar — matches the table treatment in
            // FollowUpTracker so the dialog and the underlying page
            // share one visual language for "this scrolls."
            scrollbarColor: 'rgba(187, 140, 63, 0.42) transparent',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 9 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(187, 140, 63, 0.38)',
              borderRadius: 8,
              border: '2px solid transparent',
              backgroundClip: 'content-box',
              transition: 'background 0.2s ease',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(187, 140, 63, 0.65)',
              backgroundClip: 'content-box',
            },
          }}
        >
          <Stack spacing={1.5}>
            {sorted.map((n) => (
              <TouchpointRow key={n.id} note={n} onEdit={() => onEdit(n)} />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

function TouchpointRow({ note, onEdit }: { note: VisitNote; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const tone = OUTCOME_TONE[note.outcome] ?? '#7a7a78';
  const channelLabel = VISIT_CHANNEL_LABELS[note.channel];
  const outcomeLabel = VISIT_OUTCOME_LABELS[note.outcome];

  // Show full note text after a threshold; preview otherwise.
  const PREVIEW_CHARS = 220;
  const needsTruncation = note.note_text.length > PREVIEW_CHARS;
  const visible =
    expanded || !needsTruncation ? note.note_text : `${note.note_text.slice(0, PREVIEW_CHARS)}…`;

  const visitDateLabel = formatVisitDate(note.visit_date);

  return (
    <Box
      sx={{
        pl: 1.25,
        pr: 0.5,
        py: 0.75,
        borderLeft: `3px solid ${tone}`,
        bgcolor: '#fff',
        borderRadius: '0 4px 4px 0',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        rowGap={0.5}
        sx={{ mb: 0.4 }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12,
            fontWeight: 700,
            color: INK,
          }}
        >
          {visitDateLabel}
        </Typography>
        {(() => {
          // Fall back to the storefront glyph if the backend ever sends
          // a channel this build doesn't know about — a missing icon
          // shouldn't blank the row.
          const ChannelIcon = CHANNEL_ICON[note.channel] ?? StorefrontIcon;
          return <ChannelIcon sx={{ fontSize: 13, color: 'rgba(15, 10, 7, 0.45)' }} />;
        })()}
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            color: 'rgba(15, 10, 7, 0.55)',
          }}
        >
          {channelLabel}
        </Typography>
        <Chip
          label={outcomeLabel}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: `${tone}1a`,
            color: tone,
            border: `1px solid ${tone}55`,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontStyle: 'italic',
            color: 'rgba(15, 10, 7, 0.5)',
          }}
        >
          {note.rep_name || 'Unknown rep'}
          <EditedMarker
            editedAt={note.edited_at}
            editedByName={note.edited_by_name}
            someoneElse={note.edited_by_someone_else}
          />
        </Typography>
        {note.can_edit && (
          <Tooltip title="Edit this touchpoint">
            <IconButton
              size="small"
              onClick={onEdit}
              aria-label="Edit this touchpoint"
              sx={{ p: 0.25, color: 'rgba(15, 10, 7, 0.4)', '&:hover': { color: GOLD } }}
            >
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12.5,
          color: 'rgba(15, 10, 7, 0.78)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {visible}
      </Typography>
      {needsTruncation && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((p) => !p)}
          sx={{
            mt: 0.25,
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: GOLD,
            px: 0.5,
            minWidth: 0,
            '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
          }}
        >
          {expanded ? 'Show less' : 'Show full note'}
        </Button>
      )}
    </Box>
  );
}

// =====================================================================
// Address sub-line for the dialog title.  Renders nothing until the
// detail query resolves, then shows a single gold-pinned line with
// street · city, state ZIP — whatever subset of fields the account
// actually has.  Silent when the account is genuinely address-less
// (some legacy records) so the dialog doesn't show "Address: —" noise.
// =====================================================================
function AccountAddressLine({
  summary,
  loading,
}: {
  summary: FieldAccountSummary | null;
  loading: boolean;
}) {
  if (loading || !summary) return null;

  const cityStateZip = [
    summary.city,
    [summary.state_code, summary.zip_code].filter(Boolean).join(' '),
  ]
    .filter((s) => s && s.length > 0)
    .join(', ');
  const parts = [summary.address, cityStateZip].filter((s) => s && s.length > 0);
  if (parts.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 0.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.6,
        color: 'rgba(15, 10, 7, 0.62)',
        minWidth: 0,
      }}
    >
      <PlaceIcon sx={{ fontSize: 14, color: GOLD, flexShrink: 0, mt: '2px' }} />
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12,
          fontWeight: 500,
          color: 'rgba(15, 10, 7, 0.62)',
          lineHeight: 1.4,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {parts.join(' · ')}
      </Typography>
    </Box>
  );
}

// =====================================================================
// "Next visit impact" card — concrete preview of the cooldown that
// the chosen outcome will trigger.  Reads OUTCOME_GUIDE for the
// cooldown days, formats the actual eligibility date, and reminds
// the rep that My Territories stays unaffected.  Re-rendering is
// trivial — pure derivation from the three controlled inputs.
//
// Visible to both reps and admins.  The two key things it
// communicates:
//   1. WHEN the account will resurface on Today's Plan (concrete date)
//   2. The fact that "auto-resurface" is the surface that's gated —
//      My Territories stays open so reps can still revisit on demand.
// =====================================================================
function NextVisitImpactCard({
  outcome,
  visitDate,
  channel,
  isEdit,
}: {
  outcome: VisitOutcome;
  visitDate: string;
  channel: VisitChannel;
  isEdit: boolean;
}) {
  const guide = OUTCOME_GUIDE[outcome];
  const tone = OUTCOME_TONE[outcome];
  const eligibleDate = formatEligibleDate(visitDate, guide.cooldownDays);
  const channelLabel = VISIT_CHANNEL_LABELS[channel].toLowerCase();

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.25,
        bgcolor: `${tone}0a`,
        border: `1px solid ${tone}40`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: `${tone}1a`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tone,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 800,
            fontSize: 11,
            lineHeight: 1,
            letterSpacing: '-0.01em',
          }}
        >
          {guide.cooldownDays}d
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: tone,
            mb: 0.5,
          }}
        >
          Next visit impact
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 12.5,
            color: 'rgba(15, 10, 7, 0.78)',
            lineHeight: 1.55,
          }}
        >
          {isEdit ? 'Saving this as a' : 'Logging a'} <strong>{channelLabel}</strong> with outcome{' '}
          <Box component="span" sx={{ color: tone, fontWeight: 700 }}>
            {guide.label}
          </Box>{' '}
          will suppress this account from <strong>Today’s Plan</strong> auto-picks until{' '}
          <strong>{eligibleDate}</strong> ({guide.cooldownDays}-day cooldown).
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.55)',
            lineHeight: 1.5,
          }}
        >
          It still appears in <strong>My Territories</strong> if you need to revisit sooner.
        </Typography>
      </Box>
    </Box>
  );
}
